import React, { memo } from 'react';

interface AutocompleteSuggestion {
    id: number;
    name: string;
    url: string;
    contentType: string;
}

interface AutocompleteSuggestionsProps {
    suggestions: AutocompleteSuggestion[];
    visible: boolean;
    onSuggestionClick: (suggestion: AutocompleteSuggestion) => void;
    onClose: () => void;
    highlightedIndex: number;
    query: string;
}

export const AutocompleteSuggestions: React.FC<AutocompleteSuggestionsProps> = memo(({
    suggestions,
    visible,
    onSuggestionClick,
    onClose,
    highlightedIndex,
    query
}) => {
    if (!visible || suggestions.length === 0) {
        return null;
    }

    const handleSuggestionClick = (suggestion: AutocompleteSuggestion) => {
        onSuggestionClick(suggestion);
        onClose();
    };

    // Helper to split suggestion name
    const getSuggestionParts = (name: string, query: string) => {
        if (!query) {
            return { match: '', rest: name };
        }

        const lowerName = name.toLowerCase();
        const lowerQuery = query.toLowerCase();

        // If the suggestion starts with the entire query, highlight exactly that
        if (lowerName.startsWith(lowerQuery)) {
            return {
            match: name.substring(0, query.length),
            rest: name.substring(query.length),
            };
        }

        // Otherwise, find the longest common prefix
        let i = 0;
        const max = Math.min(lowerName.length, lowerQuery.length);
        while (i < max && lowerName[i] === lowerQuery[i]) {
            i++;
        }

        // If we have at least one character in common, split at that boundary
        if (i > 0) {
            return {
            match: name.substring(0, i),
            rest: name.substring(i),
            };
        }

        // No match at all, highlight nothing
        return { match: '', rest: name };
    };

    return (
        <div className="search-autocomplete-dropdown" role="listbox">
            <ul className="search-autocomplete-list">
                {suggestions.map((suggestion, index) => {
                    const { match, rest } = getSuggestionParts(suggestion.name, query);
                    return (
                        <li
                            key={suggestion.id}
                            className={`search-autocomplete-item ${index === highlightedIndex ? 'highlighted' : ''}`}
                            role="option"
                            aria-selected={index === highlightedIndex}
                            onClick={() => handleSuggestionClick(suggestion)}
                            onMouseDown={(e) => e.preventDefault()}
                        >
                            <div className="search-autocomplete-item-content">
                                <svg className="svg-icon">
                                    <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="#icon-search" />
                                </svg>
                                <span className="search-autocomplete-item-name">
                                    {match}
                                    {rest && <b>{rest}</b>}
                                </span>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
});

AutocompleteSuggestions.displayName = 'AutocompleteSuggestions';
